import { User } from 'lucide-react';
import ScrollToTop from './ScrollToTop';
// src/InvoiceDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import NavBarCommerciale from './NavBarCommerciale';
import { motion, AnimatePresence } from 'framer-motion';
import { buildInvoicePDF } from './InvoicePDF';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fmtDate = (d) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const TYPE_LABELS = {
  acompte: 'Acompte',
  solde: 'Solde',
  echeance: 'Échéance',
};

const STATUT_STYLES = {
  generee:    { label: 'Générée',    color: '#6b7280', bg: '#f3f4f6' },
  emise:      { label: 'Émise',      color: '#f59e0b', bg: '#fef3c7' },
  en_attente: { label: 'En attente', color: '#3b82f6', bg: '#eff6ff' },
  payee:      { label: 'Payée',      color: '#10b981', bg: '#d1fae5' },
  annulee:    { label: 'Annulée',    color: '#ef4444', bg: '#fee2e2' },
};

const LOGO_URL = 'https://devis.takoussane.com/logo-takoussane.png';
const API_URL = process.env.REACT_APP_API_URL || 'https://devis.takoussane.com/api';

const InvoiceDashboard = () => {
  const navigate = useNavigate();
  const { profile, user, signOut } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterType, setFilterType] = useState('tous');
  const [toast, setToast] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data: invData } = await supabase
      .from('invoices')
      .select('*')
      .order('created_at', { ascending: false });

    const { data: qData } = await supabase
      .from('quotes')
      .select('id, quote_number, date, client_info, company_info, total_with_vat, items');

    const quotesMap = {};
    (qData || []).forEach(q => { quotesMap[q.id] = q; });

    setInvoices(invData || []);
    setQuotes(quotesMap);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = invoices.filter(inv => {
    const quote = quotes[inv.quote_id] || {};
    const clientName = quote.client_info?.clientName || quote.client_info?.name || '';
    const matchSearch = !search ||
      clientName.toLowerCase().includes(search.toLowerCase()) ||
      inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
      quote.quote_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'tous' || inv.statut === filterStatut;
    const matchType = filterType === 'tous' || inv.type === filterType;
    return matchSearch && matchStatut && matchType;
  });

  const handleStatutChange = async (id, newStatut) => {
    setUpdating(id);
    const updates = { statut: newStatut };
    if (newStatut === 'payee') updates.date_paiement = new Date().toISOString().split('T')[0];
    const { error } = await supabase.from('invoices').update(updates).eq('id', id);
    if (!error) {
      setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...updates } : inv));
      showToast('Statut mis à jour');
    } else showToast('Erreur', 'error');
    setUpdating(null);
  };

  const handleDownloadPDF = async (invoice) => {
    const quote = quotes[invoice.quote_id];
    if (!quote) { showToast('Devis introuvable', 'error'); return; }
    let logoB64 = null;
    try {
      const resp = await fetch(LOGO_URL);
      const blob = await resp.blob();
      logoB64 = await new Promise(res => {
        const r = new FileReader();
        r.onloadend = () => res(r.result);
        r.readAsDataURL(blob);
      });
    } catch (_) {}
    buildInvoicePDF(invoice, quote, logoB64).save(`${invoice.invoice_number}.pdf`);
    showToast('PDF téléchargé');
  };

  const handleSendEmail = async (invoice) => {
    const quote = quotes[invoice.quote_id];
    if (!quote) { showToast('Devis introuvable', 'error'); return; }

    const clientEmail = quote.client_info?.email || quote.client_info?.clientEmail || '';
    if (!clientEmail) {
      showToast('Email client manquant sur ce devis', 'error');
      return;
    }

    setSendingEmail(invoice.id);

    // Générer le PDF en base64
    let pdfBase64 = null;
    try {
      let logoB64 = null;
      try {
        const resp = await fetch(LOGO_URL);
        const blob = await resp.blob();
        logoB64 = await new Promise(res => {
          const r = new FileReader();
          r.onloadend = () => res(r.result);
          r.readAsDataURL(blob);
        });
      } catch (_) {}

      const pdfDoc = buildInvoicePDF(invoice, quote, logoB64);
      const pdfBlob = pdfDoc.output('blob');
      pdfBase64 = await new Promise(res => {
        const r = new FileReader();
        r.onloadend = () => res(r.result.split(',')[1]);
        r.readAsDataURL(pdfBlob);
      });
    } catch (e) {
      showToast('Erreur génération PDF : ' + e.message, 'error');
      setSendingEmail(null);
      return;
    }

    // Construire le corps de l'email
    const clientName = quote.client_info?.clientName || quote.client_info?.name || '';
    const typeLabel = invoice.type === 'echeance'
      ? `Échéance ${invoice.echeance_numero - 1}/${invoice.echeance_total - 1}`
      : TYPE_LABELS[invoice.type] || invoice.type;

    const conditionsText = invoice.conditions_reglement
      ? `Conditions de règlement : ${invoice.conditions_reglement}.`
      : '';

    const body =
      'Bonjour ' + ((quote.client_info?.attentionTitle ? quote.client_info.attentionTitle + ' ' : '') + (quote.client_info?.attentionName || clientName)).trim() + ',\n\n' +
      'Veuillez trouver ci-joint votre facture ' + typeLabel.toLowerCase() +
      ' N° ' + invoice.invoice_number +
      " d'un montant de " + fmt(invoice.montant) + ' FCFA.\n\n' +
      (conditionsText ? conditionsText + '\n\n' : '') +
'Pour toute question, n\'hésitez pas à nous contacter.\n\n' +
      'Cordialement,\n\n--\n' +
      (profile?.nom || 'TAKOUSSANE ENERGY') + '\n' +
      (profile?.fonction ? profile.fonction + '\n' : '') +
      'TAKOUSSANE ENERGY\n' +
      (profile?.telephone ? 'Tél : ' + profile.telephone + '\n' : '') +
      (profile?.mobile ? 'Mob : ' + profile.mobile + '\n' : '') +
      'Email : ' + (profile?.email || user?.email || 'contact@takoussane.com') + '\n' +
      'Web : https://takoussane.com';

    try {
      const response = await fetch(`${API_URL}/send-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: clientEmail,
          cc: ['contact@takoussane.com', ...(quote.client_info?.emailsCc || []).filter(e => e !== 'contact@takoussane.com')],
          from_name: profile?.nom || 'TAKOUSSANE ENERGY',
          from_email: profile?.email || user?.email || 'contact@takoussane.com',
          subject: 'Facture ' + typeLabel + ' ' + invoice.invoice_number + ' — TAKOUSSANE ENERGY',
          body,
          pdfBase64,
          quoteNumber: invoice.invoice_number,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur envoi');
      showToast('✅ Facture envoyée à ' + clientEmail);

      // Mettre à jour le statut en "emise" si generee
      if (invoice.statut === 'generee') {
        await supabase.from('invoices').update({ statut: 'emise' }).eq('id', invoice.id);
        setInvoices(prev => prev.map(inv => inv.id === invoice.id ? { ...inv, statut: 'emise' } : inv));
      }
    } catch (e) {
      showToast('Erreur envoi : ' + e.message, 'error');
    }

    setSendingEmail(null);
  };

  // Stats
  const totalEmises = invoices.filter(i => ['emise', 'en_attente'].includes(i.statut)).length;
  const totalPayees = invoices.filter(i => i.statut === 'payee').length;
  const montantPercu = invoices.filter(i => i.statut === 'payee').reduce((s, i) => s + (i.montant || 0), 0);
  const montantAttente = invoices.filter(i => ['emise', 'en_attente'].includes(i.statut)).reduce((s, i) => s + (i.montant || 0), 0);

  const handleSignOut = async () => { await signOut(); navigate('/login'); };

  const handleDeleteInvoice = async () => {
    if (!deleteConfirm) return;
    await supabase.from('invoices').delete().eq('id', deleteConfirm.id);
    setInvoices(prev => prev.filter(inv => inv.id !== deleteConfirm.id));
    setDeleteConfirm(null);
  };

  if (deleteConfirm) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Suppression définitive</div>
          <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.7 }}>
            Supprimer définitivement la facture<br/>
            <strong style={{ color: '#111827' }}>{deleteConfirm.num}</strong> ?<br/>
            <span style={{ color: '#ef4444', fontWeight: 600 }}>Cette action est irréversible.</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#374151' }}>Annuler</button>
          <button onClick={handleDeleteInvoice} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '8px', background: '#ef4444', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: 'white' }}>Supprimer définitivement</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: 'Segoe UI, sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
        padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h1 style={{ color: 'white', fontSize: '22px', fontWeight: 700, margin: 0 }}>
            🧾 Mes Factures
          </h1>
        </div>
        <NavBarCommerciale pageCourante="factures" />
      </div>
      <div style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto', width: '100%' }}>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Total factures',  value: invoices.length,              icon: '🧾', color: '#3b82f6' },
            { label: 'En attente',      value: totalEmises,                  icon: '⏳', color: '#f59e0b' },
            { label: 'Payées',          value: totalPayees,                  icon: '✅', color: '#10b981' },
            { label: 'Montant perçu',   value: fmt(montantPercu) + ' FCFA',  icon: '💰', color: '#8b5cf6' },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              style={{ background: 'white', borderRadius: '12px', padding: '20px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.08)', borderLeft: `4px solid ${stat.color}` }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>{stat.icon}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Montant en attente */}
        {montantAttente > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px',
            padding: '12px 16px', marginBottom: '20px', fontSize: '14px', color: '#92400e' }}>
            ⚠️ <strong>{fmt(montantAttente)} FCFA</strong> en attente de paiement sur {totalEmises} facture(s)
          </div>
        )}

        {/* Filtres */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)', marginBottom: '20px',
          display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="🔍 Rechercher client, N° facture, N° devis..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: '1', minWidth: '200px', border: '1px solid #e5e7eb',
              borderRadius: '8px', padding: '8px 12px', fontSize: '14px', outline: 'none' }} />
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
            style={{ border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '8px 12px', fontSize: '14px', outline: 'none', background: 'white' }}>
            <option value="tous">Tous les statuts</option>
            <option value="generee">Générée</option>
            <option value="emise">Émise</option>
            <option value="en_attente">En attente</option>
            <option value="payee">Payée</option>
            <option value="annulee">Annulée</option>
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            style={{ border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '8px 12px', fontSize: '14px', outline: 'none', background: 'white' }}>
            <option value="tous">Tous les types</option>
            <option value="acompte">Acompte</option>
            <option value="solde">Solde</option>
            <option value="echeance">Échéance</option>
          </select>
        </div>

        {/* Tableau */}
        <div style={{ background: 'white', borderRadius: '12px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Chargement...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🧾</div>
              <div>Aucune facture trouvée</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                  {[
                    { label: 'N° Facture', width: '14%' },
                    { label: 'Type',       width: '10%' },
                    { label: 'Devis',      width: '10%' },
                    { label: 'Client',     width: '16%' },
                    { label: 'Montant',    width: '12%' },
                    { label: 'Émise le',   width: '9%'  },
                    { label: 'Payée le',   width: '9%'  },
                    { label: 'Statut',     width: '10%' },
                    { label: 'Actions',    width: '10%' },
                  ].map(({ label, width }) => (
                    <th key={label} style={{ padding: '12px 14px', textAlign: 'left',
                      fontSize: '12px', fontWeight: 600, color: '#374151', width }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filtered.map((inv, idx) => {
                    const quote = quotes[inv.quote_id] || {};
                    const clientName = quote.client_info?.clientName || quote.client_info?.name || '—';
                    const statut = STATUT_STYLES[inv.statut] || STATUT_STYLES.emise;
                    const typeLabel = inv.type === 'echeance'
                      ? `Échéance ${inv.echeance_numero - 1}/${inv.echeance_total - 1}`
                      : TYPE_LABELS[inv.type];
                    const isSending = sendingEmail === inv.id;
                    return (
                      <motion.tr key={inv.id}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.03 }}
                        style={{ borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background='white'}>
                        <td style={{ padding: '12px 14px', fontSize: '13px',
                          fontWeight: 600, color: '#1e40af' }}>{inv.invoice_number}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px' }}>{typeLabel}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#6b7280' }}>
                          {quote.quote_number || '—'}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px' }}>{clientName}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 600 }}>
                          {fmt(inv.montant)} FCFA
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#6b7280' }}>
                          {fmtDate(inv.date_emission)}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#6b7280' }}>
                          {inv.date_paiement ? fmtDate(inv.date_paiement) : '—'}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <select value={inv.statut}
                            onChange={e => handleStatutChange(inv.id, e.target.value)}
                            disabled={updating === inv.id}
                            title={
                              inv.statut === 'generee'    ? 'Facture créée, pas encore envoyée au client' :
                              inv.statut === 'emise'      ? 'Facture envoyée au client' :
                              inv.statut === 'en_attente' ? 'Échéance future, pas encore due' :
                              inv.statut === 'payee'      ? 'Paiement reçu ✓' :
                              inv.statut === 'annulee'    ? 'Facture annulée' : ''
                            }
                            style={{ background: statut.bg, color: statut.color,
                              border: `1px solid ${statut.color}`, borderRadius: '999px',
                              padding: '4px 10px', fontSize: '11px', fontWeight: 600,
                              cursor: 'pointer', outline: 'none' }}>
                            <option value="generee">Générée</option>
                            <option value="emise">Émise</option>
                            <option value="en_attente">En attente</option>
                            <option value="payee">Payée</option>
                            <option value="annulee">Annulée</option>
                          </select>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {/* PDF */}
                            <button onClick={() => handleDownloadPDF(inv)}
                              onMouseEnter={e => { e.currentTarget.style.background='#dbeafe'; e.currentTarget.style.borderColor='#93c5fd'; }}
                              onMouseLeave={e => { e.currentTarget.style.background='#eff6ff'; e.currentTarget.style.borderColor='#bfdbfe'; }}
                              style={{ background: '#eff6ff', color: '#1e40af',
                                border: '1px solid #bfdbfe', borderRadius: '6px',
                                padding: '5px 10px', fontSize: '11px', cursor: 'pointer',
                                fontWeight: 500, transition: 'all 0.15s' }}
                              title="Télécharger le PDF de la facture">
                              📥 PDF
                            </button>
                            {/* Email */}
                            <button onClick={() => handleSendEmail(inv)}
                              disabled={isSending}
                              onMouseEnter={e => { if (!isSending) { e.currentTarget.style.background='#dcfce7'; e.currentTarget.style.borderColor='#86efac'; } }}
                              onMouseLeave={e => { if (!isSending) { e.currentTarget.style.background='#f0fdf4'; e.currentTarget.style.borderColor='#bbf7d0'; } }}
                              style={{ background: '#f0fdf4', color: '#10b981',
                                border: '1px solid #bbf7d0', borderRadius: '6px',
                                padding: '5px 10px', fontSize: '11px', cursor: isSending ? 'default' : 'pointer',
                                fontWeight: 500, transition: 'all 0.15s', opacity: isSending ? 0.6 : 1 }}
                              title="Envoyer la facture par email au client, par un simple clic">
                              {isSending ? '...' : '📧 Email'}
                            </button>
                            {profile?.role === 'manager' && (
                              <button onClick={() => setDeleteConfirm({ id: inv.id, num: inv.invoice_number })}
                                onMouseEnter={e => { e.currentTarget.style.background='#fecaca'; }}
                                onMouseLeave={e => { e.currentTarget.style.background='#fef2f2'; }}
                                style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s' }}
                                title="Supprimer cette facture">
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: '12px', fontSize: '13px', color: '#9ca3af', textAlign: 'right' }}>
          {filtered.length} facture(s) affichée(s) sur {invoices.length} au total
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 100,
              background: toast.type === 'error' ? '#ef4444' : '#10b981',
              color: 'white', borderRadius: '10px', padding: '12px 20px',
              fontSize: '14px', fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            {toast.type === 'error' ? '⚠️' : '✓'} {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer style={{ background: '#1e3a7a', color: 'rgba(255,255,255,0.60)', textAlign: 'center',
        padding: '10px 24px', fontSize: '11px', letterSpacing: '0.02em',
        borderTop: '2px solid rgba(0,0,0,0.15)', marginTop: 'auto' }}>
        SolarQuotePro<sup style={{ fontSize: '8px', opacity: 0.5, verticalAlign: 'super' }}>TM</sup>
        {' '}by Takoussane Energy — v1.0.1 — © {new Date().getFullYear()}
      </footer>
      <ScrollToTop />
    </div>
  );
};

export default InvoiceDashboard;
